"use client";

import * as React from "react";
import { DEFAULT_TODO_LIST_TAG, LANGUAGES, NATIVE_LANGUAGES, UI_LOCALE_LANGUAGES, XP_POINTS } from "@/lib/constants";
import { createContext, useCallback, useRef } from "react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-requests";
import { useDispatch, useSelector } from "react-redux";
import { RootState, store } from "@/store/store";
import { useServerNotificationSubscription } from "@/hooks/useServerNotifications";
import { ContactAddedPayload, PusherEvent, RewardReceivedPayload, TodoTaskCompletedPayload, TodoTaskCreatedPayload } from "@/lib/pusher/pusher-tools";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "@heroui/modal";
import { useUserLocale } from "@/hooks/useUserLocale";
import { Button } from "@heroui/button";
import { UserContactInfo, UserLangProgress } from "@/components/Types";
import { setNewTodoItemCreated, setShowAddingContactModal, setShowDotInHeaderTab, updateLanguageProgressStats } from "@/store/reducers/appSlice";
import { XpGainedDetailedBadge } from "@/components/ui/xp/XpGainedDetailedBadge";
import ProgressService from "@/lib/services/ProgressService";
import { loadCurrentUserLangProgress, loadTodoList } from "@/store/actions/app";
import { EventQueueItem } from "./ServiceModalsProviderEventQueueItem";
import TodoTaskCompletedModal from "@/components/dialogs/TodoTaskCompletedModal";
import TodoTaskCreatedModal from "@/components/dialogs/TodoTaskCreatedModal";
import { setChatToDoItemCompleted } from "@/store/reducers/chatSlice";
import RewardReceivedModal from "@/components/dialogs/RewardReceivedModal";
import AddingContactModal from "@/app/(public)/learning-feed/AddingContactModal";

export interface ModalsProviderProps {
    children: React.ReactNode;
}

type ModalsContextType = {

};

export const ModalsContext = createContext<ModalsContextType>({

});

export function ModalsProvider({ children }: ModalsProviderProps) {
    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.app.currentUser);
    const languageProgress = useSelector((state: RootState) => state.app.languageProgress);
    const chatToDoItemCompleted = useSelector((state: RootState) => state.chat.chatToDoItemCompleted);
    const newTodoItemCreated = useSelector((state: RootState) => state.app.newTodoItemCreated);

    const [displayEventQueue, setDisplayEventQueue] = useState<EventQueueItem[]>([]);
    const [currentQueueItem, setCurrentQueueItem] = useState<EventQueueItem | null>(null);    

    // Refs to trigger last processed objects, to avoid duplicate events
    const chatToDoItemCompletedLastId = useRef<string | null>(null);
    const newTodoItemCreatedLastId = useRef<string | null>(null);

    //
    // Handle Pusher-based events that trigger modals
    //

    useServerNotificationSubscription(PusherEvent.todoTaskCompleted, async (payload: TodoTaskCompletedPayload) => {
        setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.todoTaskCompleted, payload: payload }]);
    }, [user]);

    useServerNotificationSubscription(PusherEvent.contactAdded, async (payload: ContactAddedPayload) => {
        setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.contactAdded, payload: payload }]);
        loadCurrentUserLangProgress(dispatch, user?.learningLanguage as string); // Reload language progress (it contains the update contacts list)
        dispatch(setShowAddingContactModal({ show: true, contact: payload.contact as UserContactInfo }));
    }, [user]);

    // A special case to handle new server-side created ToDo items
    useServerNotificationSubscription(PusherEvent.todoTaskCreated, async (payload: TodoTaskCreatedPayload) => {
        // Reload todo tasks in the store if the language is the same as current
        console.log("🔍 New todo task created, language:", payload.language, "current language:", languageProgress?.language);
        if (payload.language == languageProgress?.language) {
            await loadTodoList(dispatch, payload.listTag, payload.language as keyof typeof LANGUAGES);
            setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.todoTaskCreated, payload: payload }]);

            // If it's a character-related task, show a dot in the 'contacts' header
            if (payload.todoItem.chatConfig?.characterId) {
                // Show a dot in the 'contacts' header tab to signal there's a new task for a contact
                dispatch(setShowDotInHeaderTab({ tabId: 'contacts', showDot: true }));
            }            
        }
    }, [user, languageProgress, dispatch]);

    useServerNotificationSubscription(PusherEvent.rewardReceived, async (payload: RewardReceivedPayload) => {
        setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.rewardReceived, payload: payload }]);
    }, [user]);

    //
    // Handle local state changes that trigger modals
    //

    useEffect(() => {
        if (chatToDoItemCompleted?.todoItemId && chatToDoItemCompleted.todoItemId !== chatToDoItemCompletedLastId.current) {
            chatToDoItemCompletedLastId.current = chatToDoItemCompleted.todoItemId;
            // Clear the chatToDoItemCompleted state
            setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.todoTaskCompleted, payload: {
                todoItem: {
                    _id: chatToDoItemCompleted.todoItemId,
                    xpAward: chatToDoItemCompleted.xpAward,
                    xpAwardMultiple: chatToDoItemCompleted.xpAwardMultiple,
                },
            } }]);
            dispatch(setChatToDoItemCompleted(null));

            // Handle XP award for the todo-item
            if (chatToDoItemCompleted.xpAward) {
                ProgressService.getInstance().addXpGainedEvent(dispatch, languageProgress.language, chatToDoItemCompleted.xpAward, Date.now());
            } else if (chatToDoItemCompleted.xpAwardMultiple) {
                ProgressService.getInstance().addXpGainedEvent(dispatch, languageProgress.language, chatToDoItemCompleted.xpAwardMultiple * XP_POINTS.todo.todoCompleted, Date.now());
            }
        }
    }, [chatToDoItemCompleted]);

    useEffect(() => {
        if (languageProgress && newTodoItemCreated?._id && newTodoItemCreated._id.toString() !== newTodoItemCreatedLastId.current) {
            newTodoItemCreatedLastId.current = newTodoItemCreated._id.toString();
            const payload: TodoTaskCreatedPayload = {
                listTag: DEFAULT_TODO_LIST_TAG,
                language: languageProgress.language,
                todoItem: {
                    _id: newTodoItemCreated._id,
                    title: newTodoItemCreated.title,
                    emoji: newTodoItemCreated.emoji,
                    description: newTodoItemCreated.description,
                    completedAt: newTodoItemCreated.completedAt,
                    chatConfig: newTodoItemCreated.chatConfig,
                    clickableUrl: newTodoItemCreated.clickableUrl,
                },
            };
            setDisplayEventQueue([...displayEventQueue, { type: PusherEvent.todoTaskCreated, payload }]);
            dispatch(setNewTodoItemCreated(null));
        }
    }, [newTodoItemCreated, languageProgress]);

    //
    // Handling the display event queue
    //

    useEffect(() => {
        if (displayEventQueue.length > 0) {            
            const item = displayEventQueue.shift();
            if (item) {
                console.log("🔴 Displaying event: ", item);
                handleDisplayEvent(item);
            }
        }
    }, [displayEventQueue]);

    const handleDisplayEvent = (item: EventQueueItem) => {
        // TODO: Show modal for the event
        setCurrentQueueItem(item);

        if (item.payload.progress) {
            const progressUpdate: Partial<UserLangProgress> = item.payload.progress;
            dispatch(updateLanguageProgressStats({
                experience: progressUpdate.experience ?? undefined,
                rankLevel: progressUpdate.rankLevel ?? undefined,
                experienceTillNextLevel: progressUpdate.experienceTillNextLevel ?? undefined,
            }));
            ProgressService.getInstance().onServerProgressUpdate(item.payload);
        }
    }

    const handleDismissEvent = () => {
        setCurrentQueueItem(null);
    }

    return (
        <ModalsContext.Provider value={{}}>
            {children}

            {/* TODO: Change modals to use lazy loading */}

            {currentQueueItem && currentQueueItem.type === PusherEvent.todoTaskCompleted && 
                <TodoTaskCompletedModal isOpen={true} onClose={handleDismissEvent} event={currentQueueItem} />}

            {currentQueueItem && currentQueueItem.type === PusherEvent.todoTaskCreated && 
                <TodoTaskCreatedModal isOpen={true} onClose={handleDismissEvent} event={currentQueueItem} />}

            {currentQueueItem && currentQueueItem.type === PusherEvent.rewardReceived && 
                <RewardReceivedModal isOpen={true} onClose={handleDismissEvent} event={currentQueueItem} />}

            {currentQueueItem && currentQueueItem.type === PusherEvent.contactAdded && 
                <AddingContactModal onClose={handleDismissEvent} event={currentQueueItem} />}
        </ModalsContext.Provider>
    );
}
